import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { BiNetworkChart } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import { startProfileJob, listProfileJobs, getProfileJob, cancelProfileJob } from 'Metadata/profileApi';
import RunningJobBar from 'Utils/RunningJobBar';
import { isSaasSource } from 'DataSources/datasourcesApi';
import { getSourceNomenclature } from 'Utils/nomenclature';
import SalesforceObjectsView from 'Metadata/SalesforceNetwork';
import {
  getGraphSummary, getGraphNodes, getAllGraphNodes, getGraphNode, getGraphNeighbors, listManualLinks, isGraphUnavailable,
  tableLabel, edgeKindLabel, graphSourceLabel, getDataGraph, rowFilterMap, graphHasHopLimit, applyFix, startGraphTest, withObject,
} from './dataGraphApi';
import useGraphDraft, { graphChoice } from './useGraphDraft';
import { planTags as planTagsOf, PlanTag } from './ObjectReport';
import RowFilterEditor from './RowFilterEditor';
import ManualLinksPanel from './ManualLinksPanel';
import LinkSuggestionsPanel from './LinkSuggestionsPanel';
import GraphConfigPanel from './GraphConfigPanel';
import GraphTestPanel from './GraphTestPanel';
import Breadcrumbs from 'Utils/Breadcrumbs';
import { GRAPH_BASE, sourcePath, objectPath } from './paths';

const PAGE = 100;
const PROFILE_POLL_MS = 5000;
const ACTIVE = new Set(['PENDING', 'RUNNING']);
const isActive = (j) => Boolean(j && ACTIVE.has(j.status));
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '—');
const nodeKey = (n) => `${n.schema || ''}${n.table}`;

const KindBadge = ({ kind }) => (
  <span className={`dg-badge dg-badge--${(kind || '').toLowerCase()}`}>{edgeKindLabel(kind)}</span>
);

// One data graph's page (laid out 2026-11-04, 4.2.4), top to bottom: the settings panel — name, key,
// extra starts, the graph's own manual links, everything connected or a list of objects, and the object
// selection folded inside it (a Salesforce org: the profile page's object list, same component, a row
// opens …/graphs/:graphId/object/:name; a database: a searchable table list with a selected-table panel —
// columns, Direct (1 hop) and Indirect neighbours) where a tick lists the object and a listed row says how
// it is reached — then link suggestions, then the Test (the isolation report: what each rule reached, what
// is not reached and why, what a provision would need). Everything is read through the graph (`graphId`),
// so another graph's links never show here.
//
// The page has ONE draft of the graph's settings (useGraphDraft): the settings panel and, for a LIST graph,
// the ticks and rule cells all edit it — a tick lists the object (the Test derives its rule), an untick
// drops it — and "Save settings" writes it.
const GraphDetail = () => {
  const { dataSourceId, graphId } = useParams();
  const history = useHistory();
  const { connections, isReadOnly } = useContext(AppContext);
  const connection = useMemo(() => (connections || []).find((c) => String(c.id) === String(dataSourceId)), [connections, dataSourceId]);

  const [graph, setGraph] = useState(null);           // the named data graph (tdm_data_graph)
  const [allTables, setAllTables] = useState([]);     // for the settings panel's key table and object pickers
  const [draftReport, setDraftReport] = useState(null);   // the settings' draft check: tags on the graph view
  const [summary, setSummary] = useState(null);
  const [noGraph, setNoGraph] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [nodes, setNodes] = useState({ items: [], total: 0, page: 0 });
  const [selected, setSelected] = useState(null);       // { node, columns }
  const [neighbors, setNeighbors] = useState(null);
  const [links, setLinks] = useState([]);
  // The Profile job this page is waiting on (started here, or already running when the page
  // opened); polled live like the Profile page's running rows, and the graph reloads when it ends.
  const [profileJob, setProfileJob] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [cancellingProfile, setCancellingProfile] = useState(false);
  // The connection list can arrive after the graph; the summary's own graph source settles it.
  const saas = isSaasSource(connection) || summary?.graphSource === 'SALESFORCE_DESCRIBE';
  // The page's one draft of the graph's settings; its object list is what the ticks edit.
  const hopLimited = graphHasHopLimit(connection || { datasourceType: graph && graph.datasourceType });
  // A Test writes the plan's derived rules onto the graph: re-read it so the editor shows them.
  const reloadGraph = useCallback(() => { getDataGraph(graphId).then((g) => setGraph(g)).catch(() => {}); }, [graphId]);
  // Saving starts a Test (Any: 10 records) by itself (4.2.5, 2026-11-04): the Test is what writes the rules
  // TDM derives onto the graph and says what each object reaches, so a saved graph should not sit untested
  // until someone remembers. ID in / Where clause stay a hand-run in the Test panel. The Test panel picks
  // the running test up when the graph is re-read after the start is accepted.
  const afterSave = useCallback((saved) => {
    setGraph(saved);
    if (!saved || !saved.keyTable || !saved.keyColumn || isReadOnly) return null;
    Promise.resolve(startGraphTest(saved.id, { mode: 'ANY' })).then(reloadGraph).catch(() => {});
    return 'Saved. Testing it with 10 records — the Test below shows how each object is reached and what the subset would get.';
  }, [isReadOnly, reloadGraph]);
  const draft = useGraphDraft(graph, { hopLimited, onSaved: afterSave });
  const isList = draft.form.outcome === 'LIST';
  const choice = graph && isList && !isReadOnly ? graphChoice(draft, graph) : null;
  const { setObjects } = draft;
  const onFix = useCallback((fix) => setObjects((objects) => applyFix(objects, fix)), [setObjects]);
  // The Test's Needed panel ticks an object into the draft (the Test derives its rule on the next Save).
  const onTick = useCallback((name) => setObjects((objects) => withObject(objects, name)), [setObjects]);
  const listedNames = useMemo(() => new Set(draft.form.objects.map((o) => String(o.name).toLowerCase())), [draft.form.objects]);

  useEffect(() => {
    let alive = true;
    getDataGraph(graphId)
      .then((g) => {
        if (!alive) return;
        if (g && String(g.dataSourceId) !== String(dataSourceId)) setError('This data graph belongs to another data source.');
        else setGraph(g);
      })
      .catch((e) => { if (alive) setError(e?.body?.message || e?.message || 'Failed to load the data graph'); });
    return () => { alive = false; };
  }, [dataSourceId, graphId]);

  const loadSummary = useCallback(async () => {
    try {
      const s = await getGraphSummary(dataSourceId, { graphId });
      setSummary(s);
      setNoGraph(false);
      setLinks(await listManualLinks(dataSourceId, graphId));
    } catch (e) {
      if (isGraphUnavailable(e)) setNoGraph(true); else setError(e?.message || 'Failed to load the data graph');
    } finally {
      setLoading(false);
    }
  }, [dataSourceId, graphId]);

  // Every table once, for the key and object pickers (the explorer's own list pages and filters). Every
  // page: a Salesforce profile holds every queryable object since contract 3.0, well past one page.
  useEffect(() => {
    if (!summary) return undefined;
    let alive = true;
    getAllGraphNodes(dataSourceId, {})
      .then((res) => {
        if (!alive) return;
        const items = [...((res && res.items) || [])];
        items.sort((a, b) => tableLabel(a).localeCompare(tableLabel(b)));
        setAllTables(items);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [dataSourceId, summary]);

  const loadNodes = useCallback(async (page = 0, append = false) => {
    try {
      const res = await getGraphNodes(dataSourceId, { q, filter, page, size: PAGE, sort: 'name', dir: 'asc', graphId });
      setNodes((prev) => ({
        items: append ? [...prev.items, ...(res?.items || [])] : (res?.items || []),
        total: res?.total ?? 0,
        page: res?.page ?? page,
      }));
    } catch (e) {
      if (!isGraphUnavailable(e)) setError(e?.message || 'Failed to list tables');
    }
  }, [dataSourceId, graphId, q, filter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  // A Profile already in flight for this source (started from the Profile page, or before a reload).
  useEffect(() => {
    let cancelled = false;
    listProfileJobs({ dataSourceId, size: 5 })
      .then((page) => {
        const jobs = (page && page.content) || (Array.isArray(page) ? page : []);
        const active = jobs.find(isActive);
        if (!cancelled && active) setProfileJob(active);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dataSourceId]);
  const profileRunning = isActive(profileJob);
  const profileJobId = profileJob ? profileJob.id : null;
  useEffect(() => {
    if (!profileRunning || profileJobId == null) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const j = await getProfileJob(profileJobId);   // live-syncs the work-item counters
        if (!alive || !j) return;
        if (isActive(j)) { setProfileJob(j); return; }
        setProfileJob(null);
        setCancellingProfile(false);
        if (j.status === 'SUCCEEDED') {
          loadSummary();
        } else if (j.status !== 'CANCELLED') {
          setProfileError(`Fingerprint ${String(j.status).toLowerCase()}${j.errorCode ? ` (${j.errorCode})` : ''}.`);
        }
      } catch (e) { /* keep polling */ }
    };
    const t = setInterval(tick, PROFILE_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [profileRunning, profileJobId, loadSummary]);
  useEffect(() => { if (!noGraph && summary && !saas) loadNodes(0, false); }, [loadNodes, noGraph, summary, saas]);

  const select = useCallback(async (n) => {
    try {
      const [detail, nb] = await Promise.all([
        getGraphNode(dataSourceId, n.table, n.schema, graphId),
        getGraphNeighbors(dataSourceId, n.table, { schema: n.schema, hops: 3, graphId }),
      ]);
      setSelected(detail);
      setNeighbors(nb);
    } catch (e) {
      setError(e?.message || 'Failed to load the table');
    }
  }, [dataSourceId, graphId]);

  const onLinksChanged = useCallback(async () => {
    await loadSummary();
    await loadNodes(0, false);
    if (selected?.node) select(selected.node);
  }, [loadSummary, loadNodes, select, selected]);

  const resync = async () => {
    setProfileError(null);
    try {
      const job = await startProfileJob(Number(dataSourceId));
      // Show the running bar at once (QUEUED); the poll fills in progress.
      setProfileJob(job && job.id != null ? { ...job, status: job.status || 'PENDING' } : { id: null, status: 'PENDING' });
    } catch (e) {
      setProfileError(e?.body?.message || e?.message || 'Could not start Fingerprint');
    }
  };
  const cancelProfile = async () => {
    if (profileJobId == null) return;
    setCancellingProfile(true);
    try { await cancelProfileJob(profileJobId); } catch (e) { setCancellingProfile(false); }
  };

  const tags = useMemo(() => planTagsOf(draftReport), [draftReport]);
  const onDraftReport = useCallback((r) => setDraftReport(r), []);
  const name = connection?.name || graph?.dataSourceName || `Source ${dataSourceId}`;
  const graphName = graph?.name || `Data graph ${graphId}`;
  const declaredOf = (n) => Math.max(0, (n.edgeCount?.out || 0) + (n.edgeCount?.in || 0) - (n.edgeCount?.manual || 0));
  const directParents = (neighbors?.direct || []).filter((d) => d.role === 'PARENT');
  const directChildren = (neighbors?.direct || []).filter((d) => d.role === 'CHILD');

  // The object selection, a slot of the settings panel (its "Objects" accordion). `ruleCellOf(name)` is the
  // panel's "How it's reached" cell for a listed object: a column of the Salesforce object list, a line under
  // a database table's name.
  const objectsView = ({ ruleCellOf }) => (saas ? (
    <SalesforceObjectsView
      dataSourceId={dataSourceId}
      graphId={graphId}
      nouns={getSourceNomenclature('salesforce')}
      onDescribe={resync}
      onOpenObject={(table) => history.push(objectPath(dataSourceId, graphId, table))}
      rowFilters={graph ? rowFilterMap(graph) : null}
      planTags={tags}
      selectable={Boolean(choice)}
      choice={choice}
      extraColumn={isList ? {
        title: 'How it’s reached',
        hint: 'The rule that reaches a ticked object: given by you, or derived by TDM from the org’s lookups and saved by the Test',
        render: (n) => ruleCellOf(n.table),
      } : null}
    />
  ) : (
    <div className='dg-layout'>
      <div className='dg-panel'>
        <div className='dg-panel__head'>
          <div className='dg-panel__title'>Tables · {fmt(summary.tableCount)}</div>
          <div className='dg-muted'>{fmt(summary.componentCount)} table groups</div>
        </div>
        <input className='dg-search' placeholder='Find table…' value={q} onChange={(e) => setQ(e.target.value)}
          data-testid='dg-search' />
        <div className='dg-chips'>
          {[['all', 'All'], ['connected', `Connected (${fmt(summary.connectedTableCount)})`], ['isolated', `Isolated (${fmt(summary.isolatedTableCount)})`]]
            .map(([k, label]) => (
              <button key={k} type='button' className={`dg-chip${filter === k ? ' dg-chip--on' : ''}`} onClick={() => setFilter(k)}>{label}</button>
            ))}
        </div>
        {choice && (
          <div className='dg-muted dg-note' data-testid='dg-nodes-pick-help'>
            Tick the tables this data graph saves ({choice.picked.size} listed{choice.dirty ? ', unsaved' : ''}). The Test derives how each is reached.
          </div>
        )}
        <div className='dg-nodes' role='list'>
          {nodes.items.map((n) => {
            const cell = isList ? ruleCellOf(tableLabel(n)) : null;
            return (
              <div className='dg-node-row' key={nodeKey(n)}>
                {choice && (
                  <input type='checkbox' className='dg-node__pick' aria-label={`list ${tableLabel(n)}`} data-testid={`dg-node-pick-${n.table}`}
                    checked={choice.picked.has(tableLabel(n))} onChange={() => choice.toggle(tableLabel(n))} />
                )}
                <div className='dg-node-row__main'>
                  <button type='button' role='listitem'
                    className={`dg-node${selected?.node && nodeKey(selected.node) === nodeKey(n) ? ' dg-node--on' : ''}`}
                    onClick={() => select(n)} data-testid={`dg-node-${n.table}`}>
                    <span className='dg-node__name'>{tableLabel(n)}<PlanTag tag={tags[tableLabel(n).toLowerCase()]} testId={`dg-node-tag-${n.table}`} /></span>
                    <span className='dg-node__sub'>
                      {fmt(n.columnCount)} cols{n.pkColumns?.length ? ` · PK ${n.pkColumns.join(', ')}` : ''}
                      {n.attributes?.keyPrefix ? ` · prefix ${n.attributes.keyPrefix}` : ''}
                    </span>
                    <span className='dg-node__edges'>{n.isolated ? 'isolated' : `${fmt((n.edgeCount?.out || 0) + (n.edgeCount?.in || 0))} edges`}</span>
                  </button>
                  {cell && <div className='dg-node__rule' data-testid={`dg-node-rule-${n.table}`}>{cell}</div>}
                </div>
              </div>
            );
          })}
          {nodes.items.length < nodes.total && (
            <button type='button' className='dg-link mt-2' onClick={() => loadNodes(nodes.page + 1, true)}>
              Load more ({fmt(nodes.total - nodes.items.length)} left)
            </button>
          )}
        </div>
        <div className='dg-muted dg-note'>
          Groups are connected components over declared keys + manual links; isolated tables don&apos;t count toward a group.
        </div>
      </div>

      <div className='dg-panel'>
        {!selected && <div className='dg-empty'>Pick a table to see what it links to.</div>}
        {selected && (
          <>
            <div className='dg-panel__head'>
              <div>
                <div className='dg-panel__title'>{tableLabel(selected.node)}</div>
                <div className='dg-muted'>
                  {fmt(selected.node.columnCount)} columns · {selected.node.rowCount != null ? `${fmt(selected.node.rowCount)} rows` : 'rows unknown'}
                  {selected.node.pkColumns?.length ? ` · PK ${selected.node.pkColumns.join(', ')}` : ''}
                  {selected.node.attributes?.keyPrefix ? ` · Id prefix ${selected.node.attributes.keyPrefix}` : ''}
                </div>
              </div>
              <div className='dg-chips'>
                <span className='dg-chip dg-chip--static'>{fmt(declaredOf(selected.node))} DECLARED</span>
                <span className='dg-chip dg-chip--static'>{fmt(selected.node.edgeCount?.manual)} MANUAL</span>
                {choice && (
                  <button type='button' className={`dg-chip${choice.picked.has(tableLabel(selected.node)) ? ' dg-chip--on' : ''}`}
                    data-testid='dg-selected-pick' onClick={() => choice.toggle(tableLabel(selected.node))}>
                    {choice.picked.has(tableLabel(selected.node)) ? 'Listed — remove from this data graph' : 'List in this data graph'}
                  </button>
                )}
              </div>
            </div>

            {graph && (
              <RowFilterEditor graph={graph} table={tableLabel(selected.node)} engine={connection?.type || graph.engine}
                canEdit={!isReadOnly} onSaved={setGraph} />
            )}

            <div className='dg-section'>
              <div className='dg-section__title'>Direct — 1 hop · {fmt((neighbors?.direct || []).length)} tables</div>
              {(neighbors?.direct || []).length === 0 && <div className='dg-muted'>No direct links.</div>}
              {[...directParents, ...directChildren].map((d) => (
                <div className='dg-edge' key={`${d.edge.id}-${d.role}`}>
                  <span className='dg-edge__name'>{tableLabel(d.node)}</span>
                  <span className='dg-muted'>via {d.via}</span>
                  <span className='dg-muted'>{d.role === 'PARENT' ? 'referenced' : 'references this'}</span>
                  <KindBadge kind={d.edge.kind} />
                  {d.edge.status === 'REJECTED' && <span className='dg-badge dg-badge--rejected'>REJECTED</span>}
                </div>
              ))}
            </div>

            <div className='dg-section'>
              <div className='dg-section__title'>Indirect — transitively linked · {fmt((neighbors?.indirect || []).length)} tables</div>
              {(neighbors?.indirect || []).length === 0 && <div className='dg-muted'>Nothing beyond one hop.</div>}
              {(neighbors?.indirect || []).map((i) => (
                <div className='dg-edge' key={`${tableLabel(i.node)}-${i.hops}`}>
                  <span className='dg-edge__name'>{tableLabel(i.node)}</span>
                  <span className='dg-badge dg-badge--hops'>{i.hops} HOPS</span>
                </div>
              ))}
            </div>

            <div className='dg-section'>
              <div className='dg-section__title'>Columns · {fmt(selected.columns?.length)}</div>
              <div className='dg-cols'>
                {(selected.columns || []).map((c) => (
                  <div className='dg-col' key={c.name}>
                    <span className='dg-col__name'>{c.name}</span>
                    <span className='dg-muted'>{c.dataType || ''}{c.nullable === false ? ' · not null' : ''}</span>
                    <span>
                      {c.pk && <span className='dg-badge dg-badge--pk'>PK</span>}
                      {!c.pk && c.unique && <span className='dg-badge dg-badge--pk'>UNIQUE</span>}
                      {c.edgeIds?.length ? <span className='dg-muted'> {c.edgeIds.length} link{c.edgeIds.length > 1 ? 's' : ''}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ));

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='dg-page'>
        <div className='dg-breadcrumb d-flex align-items-center flex-wrap'>
          <Breadcrumbs className='mb-0' items={[
            { label: 'Data Graph', to: GRAPH_BASE },
            { label: name, to: sourcePath(dataSourceId), testId: 'dg-source-crumb' },
            { label: graphName },
          ]} />
          {saas && (
            <>
              <span> · </span>
              <button type='button' className='dg-link' data-testid='dg-profile-view'
                title='The org on the fingerprint page: Describe history, storage, and the same objects to explore'
                onClick={() => history.push(`/metadata/source/${encodeURIComponent(dataSourceId)}`)}>
                Fingerprint view
              </button>
            </>
          )}
        </div>
        <PageHeader
          icon={BiNetworkChart}
          eyebrow={`Advanced Settings · Data graph of ${name}`}
          header={graphName}
          tooltip='Declared keys from the source plus this data graph’s manual links: the edges subsetting walks'
          meta={summary ? (
            <span>
              {fmt(summary.tableCount)} tables · {fmt(summary.declaredEdgeCount)} declared edges · {fmt(summary.manualLinkCount)} manual links
              · {graphSourceLabel(summary.graphSource)} · synced {when(summary.snapshotTakenAt)}
            </span>
          ) : null}
          primaryAction={profileRunning || noGraph
            ? null
            : <Button size='sm' variant='outline-secondary' onClick={resync} disabled={!!isReadOnly} data-testid='dg-resync' title={isReadOnly ? READ_ONLY_TITLE : 'Runs a new Fingerprint (a Describe for a Salesforce org) so the data graph picks up schema changes'}>Update fingerprint</Button>}
        />

        {profileRunning && (
          <div className='mb-3'>
            <RunningJobBar
              testId='dg-profile-running'
              jobType={saas ? 'Describe' : 'Fingerprint'}
              status={profileJob.status === 'PENDING' ? 'QUEUED' : 'RUNNING'}
              completed={profileJob.workItemsCompleted}
              total={profileJob.workItemsTotal}
              unit={(profileJob.workItemKind || (saas ? 'objects' : 'tables')).toLowerCase()}
              note={noGraph ? 'The graph appears when it completes.' : 'The graph refreshes when it completes.'}
              onCancel={profileJobId != null ? cancelProfile : null}
              cancelling={cancellingProfile}
            />
          </div>
        )}
        {profileError && <div className='dg-error mb-3' data-testid='dg-profile-error'>{profileError}</div>}

        {graph && (
          <GraphConfigPanel graph={graph} connection={connection} tables={allTables} canEdit={!isReadOnly} draft={draft}
            onSaved={(g) => setGraph(g)} onDraftReport={onDraftReport}
            manualLinks={summary ? <ManualLinksPanel dataSourceId={dataSourceId} graphId={graphId} links={links} onChanged={onLinksChanged} embedded /> : null}
            objectsView={summary ? objectsView : null} />
        )}

        {noGraph && !profileRunning && (
          <div className='dg-empty'>
            No graph yet for this source. Run Fingerprint to capture its declared keys and lookups.
            <div className='mt-2'><Button size='sm' onClick={resync} disabled={!!isReadOnly} title={isReadOnly ? READ_ONLY_TITLE : undefined} data-testid='dg-run-profile'>Run Fingerprint</Button></div>
          </div>
        )}

        {summary && (
          <LinkSuggestionsPanel dataSourceId={dataSourceId} graphId={graphId} selectedNode={selected?.node}
            onChanged={onLinksChanged} />
        )}

        {/* Last: the Test. Plans need the profile snapshot, so there is nothing to test before one exists. */}
        {graph && summary && (
          <GraphTestPanel graph={graph} canTest={!isReadOnly} dirty={draft.dirty} onStarted={reloadGraph}
            onFix={isList && !isReadOnly ? onFix : undefined} onAdd={isList && !isReadOnly ? onTick : undefined} listed={listedNames}
            engine={connection?.type || connection?.engine || graph.engine || (saas ? 'salesforce' : null)} />
        )}
      </div>
    </FetchContainer>
  );
};

export default GraphDetail;
